/**
 * Google Analytics 4 Service
 * Handles GA4 tracking with GDPR-compliant consent mode
 */

declare global {
  interface Window {
    dataLayer: any[]
    gtag: (...args: any[]) => void
  }
}

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-NSNJWVLSW8'

class GoogleAnalyticsService {
  private initialized = false
  private consentGrantedState = false

  /**
   * Check if analytics consent has been granted
   */
  get isConsentGranted(): boolean {
    return this.consentGrantedState
  }

  /**
   * Initialize GA4 with consent mode (default denied)
   * This should be called early in the app lifecycle
   */
  init(): void {
    if (this.initialized || typeof window === 'undefined') return

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || []
    window.gtag = function gtag() {
      window.dataLayer.push(arguments)
    }

    // Set default consent to denied (GDPR compliant)
    window.gtag('consent', 'default', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      wait_for_update: 500
    })

    // Initialize gtag
    window.gtag('js', new Date())
    window.gtag('config', GA_MEASUREMENT_ID, {
      send_page_view: false // We'll send page views manually for SPA
    })

    // Load the GA script
    this.loadScript()

    this.initialized = true
    console.log('🔍 Google Analytics initialized (consent pending)')
  }

  /**
   * Load the GA4 script dynamically
   */
  private loadScript(): void {
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
    document.head.appendChild(script)
  }

  /**
   * Update consent status (called when user accepts/rejects cookies)
   */
  updateConsent(analyticsAllowed: boolean, marketingAllowed: boolean = false): void {
    if (typeof window === 'undefined' || !window.gtag) return

    window.gtag('consent', 'update', {
      analytics_storage: analyticsAllowed ? 'granted' : 'denied',
      ad_storage: marketingAllowed ? 'granted' : 'denied',
      ad_user_data: marketingAllowed ? 'granted' : 'denied',
      ad_personalization: marketingAllowed ? 'granted' : 'denied'
    })

    this.consentGrantedState = analyticsAllowed
    console.log(`🔍 GA consent updated: analytics=${analyticsAllowed}, marketing=${marketingAllowed}`)

    // If consent just granted, send the current page view
    if (analyticsAllowed && this.initialized) {
      this.trackPageView(window.location.pathname, document.title)
    }
  }

  /**
   * Track page views (for SPA navigation)
   */
  trackPageView(path: string, title?: string): void {
    if (typeof window === 'undefined' || !window.gtag || !this.initialized) return

    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title || document.title,
      page_location: window.location.href
    })

    console.log(`🔍 Page view tracked: ${path}`)
  }

  /**
   * Track custom events
   */
  trackEvent(
    eventName: string,
    params?: Record<string, any>
  ): void {
    if (typeof window === 'undefined' || !window.gtag || !this.initialized) return

    window.gtag('event', eventName, params)
    console.log(`🔍 Event tracked: ${eventName}`, params)
  }

  // =====================
  // Pre-defined Events
  // =====================

  /**
   * Track user sign up
   */
  trackSignUp(method: string = 'email'): void {
    this.trackEvent('sign_up', { method })
  }

  /**
   * Track user login
   */
  trackLogin(method: string = 'email'): void {
    this.trackEvent('login', { method })
  }

  /**
   * Track service view
   */
  trackViewService(serviceId: string, serviceName: string, providerId: string): void {
    this.trackEvent('view_item', {
      items: [{
        item_id: serviceId,
        item_name: serviceName,
        item_category: 'service',
        affiliation: providerId
      }]
    })
  }

  /**
   * Track service view (alias for trackViewService)
   */
  trackServiceView(serviceId: string, serviceName: string, providerName: string): void {
    this.trackViewService(serviceId, serviceName, providerName)
  }

  /**
   * Track booking started
   * @param currency ISO 4217 code from the shop/provider snapshot. Required —
   *   callers must thread the entity's own currency through so multi-region
   *   analytics don't collapse to a single hardcoded default.
   */
  trackBeginBooking(serviceId: string, serviceName: string, price: number, currency: string): void {
    this.trackEvent('begin_checkout', {
      currency,
      value: price,
      items: [{
        item_id: serviceId,
        item_name: serviceName,
        price: price,
        quantity: 1
      }]
    })
  }

  /**
   * Track booking completed
   * @param bookingId - The booking reference ID
   * @param serviceName - The name of the service booked
   * @param price - The price of the booking
   * @param serviceId - Optional service ID
   * @param providerId - Optional provider ID
   * @param currency ISO 4217 code from the booking's currency snapshot. Required —
   *   callers must thread the booking's own currency through so purchase events
   *   report the true region rather than a hardcoded default.
   */
  trackBookingCompleted(
    bookingId: string,
    serviceName: string,
    price: number,
    serviceId: string | undefined,
    providerId: string | undefined,
    currency: string
  ): void {
    this.trackEvent('purchase', {
      transaction_id: bookingId,
      currency,
      value: price,
      items: [{
        item_id: serviceId || bookingId,
        item_name: serviceName,
        price: price,
        quantity: 1,
        affiliation: providerId || 'Zaveato'
      }]
    })
  }

  /**
   * Track search
   */
  trackSearch(searchTerm: string, resultsCount?: number): void {
    this.trackEvent('search', {
      search_term: searchTerm,
      results_count: resultsCount
    })
  }

  /**
   * Track provider registration
   */
  trackProviderRegistration(): void {
    this.trackEvent('generate_lead', {
      lead_type: 'provider_registration'
    })
  }

  /**
   * Track contact form submission
   */
  trackContactSubmission(): void {
    this.trackEvent('generate_lead', {
      lead_type: 'contact_form'
    })
  }

  /**
   * Track service search/filter
   */
  trackServiceSearch(filters: {
    searchTerm?: string
    categoryId?: string
    minPrice?: number
    maxPrice?: number
    sortBy?: string
  }): void {
    this.trackEvent('view_search_results', {
      search_term: filters.searchTerm,
      category_id: filters.categoryId,
      min_price: filters.minPrice,
      max_price: filters.maxPrice,
      sort_by: filters.sortBy
    })
  }

  /**
   * Track when user clicks "Book Service" button
   * @param currency ISO 4217 code from the service/shop snapshot. Required.
   */
  trackBookingStarted(serviceId: string, serviceName: string, price: number, currency: string): void {
    this.trackEvent('begin_checkout', {
      currency,
      value: price,
      items: [{
        item_id: serviceId,
        item_name: serviceName,
        price: price,
        quantity: 1
      }]
    })
  }

  /**
   * Track when user selects a time slot
   * @param currency ISO 4217 code from the service/provider snapshot. Required.
   */
  trackSlotSelected(serviceId: string, serviceName: string, slotDateTime: string, currency: string): void {
    this.trackEvent('add_to_cart', {
      currency,
      items: [{
        item_id: serviceId,
        item_name: serviceName,
        quantity: 1
      }],
      slot_datetime: slotDateTime
    })
  }

  /**
   * Track when user proceeds to payment
   * @param currency ISO 4217 code from the booking draft. Required.
   */
  trackPaymentInitiated(serviceId: string, serviceName: string, price: number, currency: string): void {
    this.trackEvent('add_payment_info', {
      currency,
      value: price,
      items: [{
        item_id: serviceId,
        item_name: serviceName,
        price: price,
        quantity: 1
      }]
    })
  }

  /**
   * Set user ID for logged-in users (for cross-device tracking)
   */
  setUserId(userId: string | null): void {
    if (typeof window === 'undefined' || !window.gtag) return

    if (userId) {
      window.gtag('config', GA_MEASUREMENT_ID, {
        user_id: userId
      })
      console.log('🔍 User ID set for GA')
    }
  }

  /**
   * Set user properties
   */
  setUserProperties(properties: Record<string, any>): void {
    if (typeof window === 'undefined' || !window.gtag) return

    window.gtag('set', 'user_properties', properties)
  }
}

export const googleAnalytics = new GoogleAnalyticsService()
export default googleAnalytics
